// FoundHerSpotlightAdmin.jsx
//
// Internal admin tool: review incoming FoundHer Spotlight nominations, curate
// the public-facing content, assign the founder's 14-char code, and publish
// (isActive: true) when ready.
//
// ASSUMPTION: admin-gating below checks `authState?.user?.role === "admin"`.
// Adjust this to match however your app actually flags admin users — this is
// a UX-level guard only; the real enforcement must happen server-side via the
// requireAdmin middleware on the /admin/* routes.

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClock, faSave, faEye } from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../../../hooks/useFetch";

const ADMIN_LIST_ENDPOINT = "/foundher/spotlight/admin/list";
const ADMIN_UPDATE_ENDPOINT = (id) => `/foundher/spotlight/admin/${id}`;

const MAX_CONTENT_LENGTH = 10000;

const emptyDraft = {
  founderName: "",
  ventureName: "",
  category: "",
  tagline: "",
  image: "",
  founderImage: "",
  impact: "",
  globalReach: "",
  spotlightContent: "",
  foundHerCode: "",
  isActive: false,
};

export default function FoundHerSpotlightAdmin() {
  const authState = useSelector((state) => state.authReducer);
  const token = authState?.token;
  const isAdmin = authState?.user?.think5whyscode === "TU243BM5850150"; 
  const [fetchData] = useFetch();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // "pending" | "published" | "all"

  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [saveStatus, setSaveStatus] = useState(""); // "", "saving", "saved", "error"
  const [saveError, setSaveError] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const query = filter === "all" ? "" : `?isActive=${filter === "published"}`;
      const config = {
        url: `${ADMIN_LIST_ENDPOINT}${query}`,
        method: "get",
        headers: { Authorization: token },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        setRequests(response.records || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isAdmin]);

  const openRequest = (req) => {
    setSelected(req);
    setDraft({
      founderName: req.founderName || "",
      ventureName: req.ventureName || "",
      category: req.category || "",
      tagline: req.tagline || "",
      image: req.image || "",
      founderImage: req.founderImage || "",
      impact: req.impact || "",
      globalReach: req.globalReach || "",
      spotlightContent: req.spotlightContent || "",
      foundHerCode: req.foundHerCode || "",
      isActive: Boolean(req.isActive),
    });
    setSaveStatus("");
    setSaveError("");
    setShowPreview(false);
  };

  const handleDraftChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDraft((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async (publish) => {
    if (!selected) return;

    const nextIsActive = publish ?? draft.isActive;

    if (draft.foundHerCode && draft.foundHerCode.length !== 14) {
      setSaveStatus("error");
      setSaveError("foundHerCode must be exactly 14 characters.");
      return;
    }
    if (nextIsActive && (!draft.foundHerCode || !draft.spotlightContent)) {
      setSaveStatus("error");
      setSaveError("Assign a foundHerCode and write spotlightContent before publishing.");
      return;
    }

    setSaveStatus("saving");
    setSaveError("");
    try {
      const config = {
        url: ADMIN_UPDATE_ENDPOINT(selected._id || selected.id),
        method: "put",
        headers: { Authorization: token },
        data: { ...draft, isActive: nextIsActive },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        setSaveStatus("saved");
        setDraft((prev) => ({ ...prev, isActive: nextIsActive }));
        loadRequests();
      } else {
        setSaveStatus("error");
        setSaveError(response?.message || "Save failed.");
      }
    } catch (err) {
      console.log(err);
      setSaveStatus("error");
      setSaveError("Save failed.");
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-200">You don't have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 text-white p-4 md:p-8">
      <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-violet-400 bg-clip-text text-transparent mb-1">FoundHer Spotlight — Admin</h1>
      <p className="text-sm text-gray-200 mb-6">
        Review nominations, curate the public spotlight content, and publish when ready.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Requests list */}
        <div className="lg:col-span-2 bg-zinc-800 rounded-2xl shadow p-4">
          <div className="flex gap-2 mb-4">
            {["pending", "published", "all"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`w-fit px-2 py-1 rounded-full text-xs font-medium capitalize ${
                  filter === f ? "bg-violet-600 text-white" : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-sm text-gray-100 py-6 text-center">Loading requests...</p>
          ) : requests.length === 0 ? (
            <p className="text-sm text-gray-100 py-6 text-center">No requests in this view.</p>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {requests.map((req) => (
                <button
                  key={req._id || req.id}
                  onClick={() => openRequest(req)}
                  className={`w-full text-left p-3 rounded-xl border transition ${
                    selected && (selected._id || selected.id) === (req._id || req.id)
                      ? "border-violet-400 bg-violet-900/40"
                      : "border-zinc-700 bg-zinc-700/60 hover:border-violet-500"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-300">
                      {req.founderName}
                    </span>
                    {req.isActive ? (
                      <span className="flex items-center gap-1 text-xs text-green-400">
                        <FontAwesomeIcon icon={faCheckCircle} /> Published
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <FontAwesomeIcon icon={faClock} /> Pending
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-200">{req.ventureName}</p>
                  <p className="text-xs text-gray-100 mt-1">
                    {req.location?.city}{req.location?.city && req.location?.state ? ", " : ""}
                    {req.location?.state}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        <div className="lg:col-span-3 bg-zinc-800 rounded-2xl shadow p-6">
          {!selected ? (
            <p className="text-sm text-gray-100 py-10 text-center">
              Select a request on the left to curate it.
            </p>
          ) : (
            <>
              <div className="mb-4 p-3 bg-zinc-900/60 border border-zinc-700 rounded-xl text-xs text-zinc-300">
                <p className="text-gray-100"><strong>Requester's note:</strong> {selected.nominationNote}</p>
                <p className="text-gray-100">
                  <strong>Requested by:</strong> {selected.requesterCode || "—"}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input
                  name="founderName"
                  value={draft.founderName}
                  onChange={handleDraftChange}
                  placeholder="Founder name"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="ventureName"
                  value={draft.ventureName}
                  onChange={handleDraftChange}
                  placeholder="Venture name"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="category"
                  value={draft.category}
                  onChange={handleDraftChange}
                  placeholder="Category (e.g. Food & Heritage)"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="tagline"
                  value={draft.tagline}
                  onChange={handleDraftChange}
                  placeholder="Tagline"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="image"
                  value={draft.image}
                  onChange={handleDraftChange}
                  placeholder="Banner image URL"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="founderImage"
                  value={draft.founderImage}
                  onChange={handleDraftChange}
                  placeholder="Founder photo URL"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="impact"
                  value={draft.impact}
                  onChange={handleDraftChange}
                  placeholder="Impact stat"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="globalReach"
                  value={draft.globalReach}
                  onChange={handleDraftChange}
                  placeholder="Global reach stat"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
                />
                <input
                  name="foundHerCode"
                  value={draft.foundHerCode}
                  onChange={handleDraftChange}
                  maxLength={14}
                  placeholder="14-char FoundHer code"
                  className="border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm sm:col-span-2 font-mono focus:border-violet-400 focus:outline-none"
                />
              </div>

              <div className="mb-1 flex items-center justify-between">
                <label className="text-sm font-medium text-zinc-300">
                  Spotlight content (HTML)
                </label>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs ${
                      draft.spotlightContent.length > MAX_CONTENT_LENGTH ? "text-red-400" : "text-zinc-400"
                    }`}
                  >
                    {draft.spotlightContent.length}/{MAX_CONTENT_LENGTH}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowPreview((p) => !p)}
                    className="flex items-center gap-1 w-fit p-1 text-xs text-violet-300 hover:text-violet-200"
                  >
                    <FontAwesomeIcon icon={faEye} /> {showPreview ? "Hide preview" : "Preview"}
                  </button>
                </div>
              </div>
              <textarea
                name="spotlightContent"
                value={draft.spotlightContent}
                onChange={handleDraftChange}
                maxLength={MAX_CONTENT_LENGTH}
                placeholder="<div>Write the founder's story here — basic HTML tags allowed (p, b, i, a, ul/li, img, etc.)</div>"
                className="w-full border border-zinc-700 bg-zinc-900 text-white placeholder-zinc-500 rounded-lg px-3 py-2 text-sm h-40 font-mono resize-y focus:border-violet-400 focus:outline-none"
              />

              {showPreview && (
                <div className="mt-3 p-4 border border-dashed border-violet-700/50 rounded-xl bg-zinc-900/60">
                  <p className="text-xs font-medium text-violet-300 mb-2">Live preview</p>
                  <div
                    className="text-sm text-violet-100 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: draft.spotlightContent }}
                  />
                </div>
              )}

              <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={draft.isActive}
                  onChange={handleDraftChange}
                />
                Published (visible on the public Spotlight page)
              </label>

              {saveStatus === "error" && (
                <p className="text-xs text-red-400 mt-2">{saveError}</p>
              )}
              {saveStatus === "saved" && (
                <p className="text-xs text-green-400 mt-2">Saved.</p>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => handleSave(false)}
                  disabled={saveStatus === "saving"}
                  className="flex-1 flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl border border-violet-500 text-violet-300 hover:bg-violet-900/40 text-sm font-medium disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faSave} /> Save Draft
                </button>
                <button
                  onClick={() => handleSave(true)}
                  disabled={saveStatus === "saving"}
                  className="flex-1 flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-pink-500 to-violet-500 text-white text-sm font-medium hover:brightness-110 disabled:opacity-60"
                >
                  <FontAwesomeIcon icon={faCheckCircle} /> Publish
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}